import { useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '../lib/turnstile';

/**
 * Cloudflare Turnstile widget. Loads the script once per page and renders a
 * widget that reports its token via `onToken`. Renders nothing when the site
 * key is not configured (VITE_TURNSTILE_SITE_KEY empty).
 */

interface TurnstileRenderOptions {
    sitekey: string;
    theme?: 'light' | 'dark' | 'auto';
    size?: 'normal' | 'compact' | 'flexible';
    callback?: (token: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: () => void;
}

interface TurnstileApi {
    render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
    reset: (widgetId?: string) => void;
    remove: (widgetId: string) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
        __ftTurnstileReady?: () => void;
    }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__ftTurnstileReady&render=explicit';

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
        window.__ftTurnstileReady = () => {
            if (window.turnstile) resolve(window.turnstile);
            else reject(new Error('Turnstile failed to initialise'));
        };
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
            scriptPromise = null;
            reject(new Error('Could not load the verification widget'));
        };
        document.head.appendChild(script);
    });
    return scriptPromise;
}

interface TurnstileProps {
    /** Called with a fresh token, or an empty string when the token expires/errors. */
    onToken: (token: string) => void;
    /** Bump this number to force the widget to reset (e.g. after a failed submit). */
    resetKey?: number;
}

export function Turnstile({ onToken, resetKey = 0 }: TurnstileProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetId = useRef<string | null>(null);
    const onTokenRef = useRef(onToken);
    // Kept in a ref so re-rendering the parent never re-creates the widget.
    useEffect(() => { onTokenRef.current = onToken; }, [onToken]);

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY) return;
        let cancelled = false;
        const container = containerRef.current;
        loadTurnstile().then((api) => {
            if (cancelled || !container) return;
            widgetId.current = api.render(container, {
                sitekey: TURNSTILE_SITE_KEY,
                theme: 'auto',
                size: 'flexible',
                callback: (token) => onTokenRef.current(token),
                'expired-callback': () => onTokenRef.current(''),
                'error-callback': () => onTokenRef.current(''),
            });
        }).catch((err: unknown) => {
            console.warn(err);
            onTokenRef.current('');
        });
        return () => {
            cancelled = true;
            if (widgetId.current && window.turnstile) {
                try { window.turnstile.remove(widgetId.current); } catch { /* already gone */ }
            }
            widgetId.current = null;
        };
    }, []);

    useEffect(() => {
        if (resetKey > 0 && widgetId.current && window.turnstile) {
            window.turnstile.reset(widgetId.current);
            onTokenRef.current('');
        }
    }, [resetKey]);

    if (!TURNSTILE_SITE_KEY) return null;
    return <div ref={containerRef} className="turnstile-widget" aria-label="Verification" />;
}
